const API_URL = process.env.REACT_APP_API_URL;
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

// Get token expiry from JWT
function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000; // Convert to milliseconds
    } catch (err) {
        console.error('Failed to parse token:', err);
        return 0;
    }
}

// Check if token needs refresh
function shouldRefreshToken() {
    const token = localStorage.getItem('clientToken');
    if (!token) return false;

    const expiry = getTokenExpiry(token);
    const now = Date.now();
    return (expiry - now) < TOKEN_REFRESH_THRESHOLD;
}

// Refresh the token
async function refreshAccessToken() {
    const refreshToken = localStorage.getItem('clientRefreshToken');
    if (!refreshToken) return false;

    try {
        const response = await fetch(`${API_URL}/client/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        });

        if (response.ok) {
            const data = await response.json();
            localStorage.setItem('clientToken', data.token);
            localStorage.setItem('clientAccessToken', data.accessToken);
            return true;
        }
    } catch (err) {
        console.error('Token refresh failed:', err);
    }
    return false;
}

// Current access token for Cognito self-service (MFA) calls. These endpoints
// need the ACCESS token, not the ID token apiFetch sends, so they cannot go
// through apiFetch - this helper gives them the same refresh-before-use
// behaviour. Returns null when there is no session or the refresh failed;
// callers should treat that as logged-out.
async function getFreshAccessToken() {
    if (shouldRefreshToken()) {
        const ok = await refreshAccessToken();
        if (!ok) return null;
    }
    return localStorage.getItem('clientAccessToken');
}

// Clear all auth data
function clearTokens() {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientAccessToken');
    localStorage.removeItem('clientRefreshToken');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientLeadId');
    localStorage.removeItem('clientTimestamp');
    localStorage.removeItem('clientRole');
}

/**
 * GATE 0 for the client portal's one live surface (LIV, §9).
 *
 * The client twin of `adminSessionUsable`, and for the same reasons: it renews
 * the session when that is all it needs, and returns false rather than letting
 * `apiClient`'s 401 path redirect a tab nobody has touched.
 * `refreshAccessToken` returns a boolean and never navigates.
 *
 * ⚠️ SINGLE-FLIGHT, as the admin twin is: one renewal shared by every caller
 * that arrives while it is in flight, the slot cleared when it settles.
 */
let renewalInFlight = null;

async function clientSessionUsable() {
    const token = localStorage.getItem('clientToken');
    if (token && getTokenExpiry(token) > Date.now()) return true;
    if (!renewalInFlight) {
        renewalInFlight = refreshAccessToken().finally(() => { renewalInFlight = null; });
    }
    return renewalInFlight;
}

export {
    shouldRefreshToken, refreshAccessToken, clearTokens, getTokenExpiry,
    getFreshAccessToken, clientSessionUsable
};
